import { Button, Input, Label } from "@monbits/ui";
import * as React from "react";
import LinkInput, { validateUrl } from "src/components/link-input/link-input";
import type { WebhookSettings } from "src/types";

interface Props {
	onFinish: (values: WebhookSettings) => Promise<void>;
	webhookSettings?: WebhookSettings;
	isFormSubmitting: boolean;
}

interface FormErrors {
	url?: string;
	secret?: string;
}

const linkPrefix = "https://";

const WebhookSettingsForm: React.FC<Props> = (props: Props) => {
	const secretId = React.useId();
	const [formData, setFormData] = React.useState<Partial<WebhookSettings>>({
		url: "",
		secret: "",
	});
	const [errors, setErrors] = React.useState<FormErrors>({});

	React.useEffect(() => {
		if (props.webhookSettings) {
			setFormData({
				url: props.webhookSettings.url.startsWith(linkPrefix)
					? props.webhookSettings.url.slice(linkPrefix.length)
					: props.webhookSettings.url,
				secret: props.webhookSettings.secret,
			});
		}
	}, [props.webhookSettings]);

	const handleInputChange = (field: keyof WebhookSettings, value: string) => {
		setFormData((prev) => ({ ...prev, [field]: value }));
		if (errors[field]) {
			setErrors((prev) => ({ ...prev, [field]: undefined }));
		}
	};

	const validateForm = (): boolean => {
		const newErrors: FormErrors = {};

		if (!formData.url?.trim()) {
			newErrors.url = "Webhook URL is required.";
		} else if (!validateUrl(formData.url, true)) {
			newErrors.url = "Enter a valid URL like example.com/webhook.";
		}

		setErrors(newErrors);
		return Object.keys(newErrors).length === 0;
	};

	const onSubmit = async (e: React.FormEvent) => {
		e.preventDefault();

		if (!validateForm()) {
			return;
		}

		const values: WebhookSettings = {
			url: linkPrefix + formData.url,
			secret: formData.secret || "",
		};

		await props.onFinish(values);
	};

	return (
		<form onSubmit={onSubmit} className="space-y-6">
			<div className="space-y-4">
				<LinkInput
					label="Webhook URL"
					name="url"
					placeholder="your-store.com/hook"
					required
					value={formData.url}
					onChange={(value) => handleInputChange("url", value)}
					error={errors.url}
				/>
				<div className="space-y-2">
					<Label htmlFor={secretId}>Webhook secret</Label>
					<Input
						id={secretId}
						name="secret"
						value={formData.secret}
						onChange={(e) => handleInputChange("secret", e.target.value)}
					/>
					<div className="space-y-2 text-sm text-muted-foreground">
						<p>
							Optional. If set, we sign each request with HMAC so you can verify
							it came from us.
						</p>
						<a
							href="https://api-docs.pay.monbits.com/#description/webhook-security"
							target="_blank"
							rel="noopener noreferrer"
							className="text-primary hover:underline inline-block"
						>
							Learn how to verify signatures
						</a>
					</div>
				</div>
			</div>
			<Button type="submit" className="sr-only">
				Submit
			</Button>
		</form>
	);
};

export default WebhookSettingsForm;
